// API_Documentation.js — API (refonte T4).
// Migrée sur le design system (dark mode) + filtre par module (info.scanbank.module).
// Parseur Postman et console de test (fetch + auth token) CONSERVÉS. Mécanisme JSON conservé.
import React, { useMemo, useState } from 'react';
import {
  Lock, Code, FileJson, AlertTriangle, ChevronDown, ChevronRight, Check,
  PlayCircle, Trash, MessageSquare, Server, Layers, Briefcase, Bug, TrendingUp,
  Activity, Loader, FileText, AlertCircle, GitBranch, ShieldCheck, Download, Lightbulb,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, Button, Badge, SearchBar, EmptyState } from '../components/ui';
import CodeBlock from '../components/ui/CodeBlock';
import { ModuleFilter, ModuleBadge } from '../components/doc';
import { moduleOf } from '../theme/tokens';

const apiContext = require.context('../data/api', false, /\.json$/);

const CATEGORY_ICONS = {
  domaines: Layers, modules: Briefcase, sessions: Activity, controles: ShieldCheck, 'contrôles': ShieldCheck,
  'controles executes': ShieldCheck, 'contrôles exécutés': ShieldCheck, 'control executions': ShieldCheck,
  'definitions de controles': Code, 'définitions de contrôles': Code, 'control definitions': Code,
  anomalies: Bug, recommandations: Lightbulb, recommendations: Lightbulb, rapports: FileText, reports: FileText,
  exports: Download, workflows: GitBranch, workflow: GitBranch, authentification: Lock, authentication: Lock,
  utilisateurs: Lock, users: Lock, roles: Lock, 'rôles': Lock, permissions: Lock, organisations: Lock,
  supervision: TrendingUp, notifications: MessageSquare, data: FileJson, "données d'audit": FileJson,
  upload: Download, 'dépôt de fichiers': Download,
};
const categoryIcon = (c) => CATEGORY_ICONS[(c || '').toLowerCase()] || Activity;

const METHOD_COLOR = { GET: '#10B981', POST: '#3B82F6', PUT: '#F59E0B', DELETE: '#EF4444', PATCH: '#8B5CF6' };

// Module d'une collection : info.scanbank.module (repli legacy info.automata.module) > déduit du nom de fichier.
const moduleOfCollection = (data, filename) => {
  const m = data.info?.scanbank?.module || data.info?.automata?.module;
  if (m) return m;
  const s = `${filename} ${data.info?.name || ''}`.toLowerCase();
  if (s.includes('anomal') || s.includes('recommend')) return 'AUDIT';
  if (s.includes('report') || s.includes('export')) return 'REPORTS';
  if (s.includes('audit')) return 'AUDIT';
  if (s.includes('security') || s.includes('kgtech')) return 'TRANSVERSE';
  if (s.includes('workflow')) return 'AUDIT';
  return 'TRANSVERSE';
};

// Parseur Postman -> endpoints unifiés (conservé).
const parsePostman = (collection) => {
  const endpoints = [];
  const walk = (items, parentPath = '') => {
    items.forEach((item) => {
      if (item.item) { walk(item.item, item.name); return; }
      if (!item.request) return;
      const req = item.request;
      const ep = {
        id: item.name.toLowerCase().replace(/\s+/g, '-'),
        category: parentPath || 'General',
        name: item.name,
        method: req.method,
        path: req.url?.raw || '',
        description: req.description || item.name,
        headers: req.header || [],
        security: req.header?.find((h) => h.key === 'Authorization') ? ['Bearer Token'] : [],
        requestBody: null,
        parameters: [],
        responses: [],
      };
      if (req.url) {
        ep.path = (ep.path || '').replace('{{baseUrl}}', '').split('?')[0];
        if (req.url.query) ep.parameters = req.url.query.map((q) => ({ name: q.key, in: 'query', description: q.description || q.key, value: q.value }));
      }
      if (req.body?.raw) {
        try { ep.requestBody = { type: 'application/json', example: JSON.parse(req.body.raw) }; }
        catch (e) { ep.requestBody = { type: 'text/plain', example: req.body.raw }; }
      }
      ep.responses = item.response?.length
        ? item.response.map((r) => { let ex = null; try { ex = r.body ? JSON.parse(r.body) : null; } catch (e) { ex = r.body || null; } return { status: r.code || 200, description: r.name || 'Success', example: ex }; })
        : [{ status: 200, description: 'Success' }];
      endpoints.push(ep);
    });
  };
  if (collection.item) walk(collection.item);
  return {
    metadata: { title: collection.info?.name, description: collection.info?.description, version: collection.info?.schema },
    endpoints,
  };
};

const JsonBlock = ({ data, title }) => (
  <div>
    {title && <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{title}</div>}
    <CodeBlock code={data} language="json" />
  </div>
);

const EndpointDoc = ({ endpoint, serverConfig, authToken, onAuthTokenUpdate }) => {
  const [open, setOpen] = useState(false);
  const [testReq, setTestReq] = useState(endpoint.requestBody ? JSON.stringify(endpoint.requestBody.example, null, 2) : '');
  const [resp, setResp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const CatIcon = categoryIcon(endpoint.category);

  const fullUrl = () => serverConfig ? `${serverConfig.protocol}://${serverConfig.host}:${serverConfig.port}${endpoint.path}` : endpoint.path;

  const test = async () => {
    if (!serverConfig) { setErr('Configuration du serveur manquante'); return; }
    setLoading(true); setErr(null); setResp(null);
    try {
      const headers = { 'Content-Type': 'application/json' };
      (endpoint.headers || []).forEach((h) => {
        if (h.key && h.value) {
          let v = h.value;
          if (h.value.includes('{{accessToken}}') && authToken) v = h.value.replace('{{accessToken}}', authToken);
          else v = h.value.replace(/\{\{.*?\}\}/g, '');
          headers[h.key] = v;
        }
      });
      if (authToken && endpoint.security?.includes('Bearer Token')) headers.Authorization = `Bearer ${authToken}`;
      const options = { method: endpoint.method, headers };
      if (endpoint.requestBody && testReq) options.body = testReq;
      const r = await fetch(fullUrl(), options);
      const data = await r.json();
      const out = { status: r.status, statusText: r.statusText, data, headers: Object.fromEntries(r.headers.entries()) };
      if (endpoint.name?.toLowerCase().includes('login') && r.status >= 200 && r.status < 300) {
        const tok = ['accessToken', 'token', 'access_token', 'authToken'].map((f) => data[f]).find(Boolean);
        if (tok && onAuthTokenUpdate) { onAuthTokenUpdate(tok); out.tokenSaved = true; }
      }
      setResp(out);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  };

  return (
    <Card className="mb-4">
      <CardHeader className="cursor-pointer" onClick={() => setOpen(!open)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 min-w-0">
            <CatIcon className="h-5 w-5 text-blue-500 shrink-0" />
            <Badge color={METHOD_COLOR[endpoint.method]}>{endpoint.method}</Badge>
            <div className="min-w-0">
              <div className="font-mono text-sm text-gray-800 dark:text-gray-100 truncate">{endpoint.path}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{endpoint.name}</div>
            </div>
          </div>
          {open ? <ChevronDown className="h-5 w-5 text-gray-400" /> : <ChevronRight className="h-5 w-5 text-gray-400" />}
        </div>
      </CardHeader>
      {open && (
        <CardContent className="space-y-6">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-1">Description</h4>
            <p className="text-gray-600 dark:text-gray-300">{endpoint.description}</p>
          </div>

          {endpoint.parameters?.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">Paramètres</h4>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-2">
                {endpoint.parameters.map((p, i) => (
                  <div key={i}><span className="font-mono text-sm font-medium text-gray-800 dark:text-gray-100">{p.name}</span><span className="text-gray-500 dark:text-gray-400 ml-2">({p.in})</span><p className="text-sm text-gray-600 dark:text-gray-300">{p.description}</p></div>
                ))}
              </div>
            </div>
          )}

          {endpoint.requestBody && (
            <div><h4 className="font-medium text-gray-900 dark:text-white mb-2">Corps de la requête</h4><JsonBlock data={endpoint.requestBody.example} title="Exemple" /></div>
          )}

          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Réponses</h4>
            <div className="space-y-3">
              {endpoint.responses.map((r, i) => (
                <div key={i}>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge color={r.status >= 200 && r.status < 300 ? '#10B981' : '#EF4444'}>{r.status}</Badge>
                    <span className="text-gray-600 dark:text-gray-300 text-sm">{r.description}</span>
                  </div>
                  {r.example && <JsonBlock data={r.example} />}
                </div>
              ))}
            </div>
          </div>

          {endpoint.security?.length > 0 && (
            <div><h4 className="font-medium text-gray-900 dark:text-white mb-2">Sécurité</h4><div className="flex gap-2">{endpoint.security.map((s, i) => <Badge key={i} color="#8B5CF6">{s}</Badge>)}</div></div>
          )}

          {/* Console de test */}
          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <h3 className="font-medium text-gray-900 dark:text-white mb-3">Console de test</h3>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">URL de test</label>
            <div className="p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg font-mono text-sm text-gray-700 dark:text-gray-200 break-all">{fullUrl()}</div>
            {endpoint.requestBody && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Corps de la requête</label>
                <textarea rows={6} value={testReq} onChange={(e) => setTestReq(e.target.value)} className="w-full p-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
              </div>
            )}
            <div className="flex justify-end mt-3">
              <Button onClick={test} disabled={loading}>
                {loading ? <><Loader className="h-4 w-4 animate-spin" /> Test en cours…</> : <><PlayCircle className="h-4 w-4" /> Tester</>}
              </Button>
            </div>
            {resp && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <Badge color={resp.status >= 200 && resp.status < 300 ? '#10B981' : '#EF4444'}>{resp.status} {resp.statusText}</Badge>
                  {resp.tokenSaved && <span className="flex items-center text-green-600 dark:text-green-400 text-sm"><Check className="h-4 w-4 mr-1" />Token sauvegardé</span>}
                </div>
                <JsonBlock data={resp.data} title="Corps de la réponse" />
              </div>
            )}
            {err && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-500/10 rounded-lg">
                <div className="flex items-center gap-2 text-red-600 dark:text-red-400"><AlertCircle className="h-5 w-5" /><span className="font-medium">Erreur de test</span></div>
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{err}</p>
                <p className="mt-1 text-xs text-red-500">Vérifiez l'accessibilité du serveur et la configuration CORS.</p>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

const DocumentationAPI = () => {
  const collections = useMemo(() => {
    return apiContext.keys().map((key) => {
      const filename = key.replace('./', '');
      try {
        const data = apiContext(key);
        if (!data.info || !Array.isArray(data.item)) return null;
        return { filename, name: data.info.name || filename, module: moduleOfCollection(data, filename), parsed: parsePostman(data) };
      } catch (e) { return null; }
    }).filter(Boolean).sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const [moduleFilter, setModuleFilter] = useState(null);
  const [selectedFilename, setSelectedFilename] = useState(collections[0]?.filename || null);
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [serverConfig, setServerConfig] = useState({ protocol: 'http', host: 'localhost', port: '8088' });
  const [authToken, setAuthToken] = useState(() => { try { return localStorage.getItem('api_auth_token') || ''; } catch (e) { return ''; } });

  const saveToken = (t) => { setAuthToken(t); try { localStorage.setItem('api_auth_token', t); } catch (e) { /* ignore */ } };
  const clearToken = () => { setAuthToken(''); try { localStorage.removeItem('api_auth_token'); } catch (e) { /* ignore */ } };

  const visible = collections.filter((c) => !moduleFilter || c.module === moduleFilter);
  const selected = visible.find((c) => c.filename === selectedFilename) || visible[0] || null;
  const categories = selected ? ['all', ...new Set(selected.parsed.endpoints.map((e) => e.category))] : ['all'];
  const endpoints = (selected?.parsed.endpoints || []).filter((e) =>
    (category === 'all' || e.category === category) &&
    (e.name.toLowerCase().includes(search.toLowerCase()) || e.path.toLowerCase().includes(search.toLowerCase()) || e.description.toLowerCase().includes(search.toLowerCase()))
  );

  if (collections.length === 0) return <EmptyState icon={FileJson} title="Aucune collection API" message="Ajoutez des collections Postman dans src/data/api/." />;

  return (
    <div>
      {/* Configuration serveur */}
      <Card className="mb-6">
        <CardHeader><CardTitle className="flex items-center gap-2"><Server className="h-5 w-5 text-blue-500" />Configuration du serveur</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Protocole</label>
              <select value={serverConfig.protocol} onChange={(e) => setServerConfig({ ...serverConfig, protocol: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500"><option value="http">HTTP</option><option value="https">HTTPS</option></select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Hôte / IP</label>
              <input value={serverConfig.host} onChange={(e) => setServerConfig({ ...serverConfig, host: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Port</label>
              <input value={serverConfig.port} onChange={(e) => setServerConfig({ ...serverConfig, port: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="flex items-end">
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">URL de base</label>
                <div className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md font-mono text-sm truncate">{`${serverConfig.protocol}://${serverConfig.host}:${serverConfig.port}`}</div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Token d'authentification</label>
              {authToken && <Button variant="ghost" size="sm" onClick={clearToken} className="text-red-600 hover:text-red-700"><Trash className="h-4 w-4" />Effacer</Button>}
            </div>
            <div className="flex gap-2">
              <input value={authToken} onChange={(e) => saveToken(e.target.value)} placeholder="Bearer token…" className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500 font-mono text-sm" />
              <div className={`px-3 py-2 rounded-md text-sm ${authToken ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>{authToken ? <span className="flex items-center"><Lock className="h-4 w-4 mr-1" />Token actif</span> : 'Pas de token'}</div>
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Le token est sauvegardé automatiquement après un login réussi.</p>
          </div>
        </CardContent>
      </Card>

      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Documentation API</h1>
        <p className="mt-1 text-gray-600 dark:text-gray-300">{collections.length} collection(s) Postman.</p>
      </div>

      <ModuleFilter value={moduleFilter} onChange={(m) => { setModuleFilter(m); setCategory('all'); }} className="mb-4" />

      {/* Sélecteur de collection */}
      <div className="flex flex-wrap gap-2 mb-6">
        {visible.map((c) => {
          const active = selected?.filename === c.filename;
          const m = moduleOf(c.module);
          return (
            <button key={c.filename} onClick={() => { setSelectedFilename(c.filename); setCategory('all'); }}
              style={active ? { backgroundColor: m.color, borderColor: m.color, color: '#fff' } : undefined}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap flex items-center gap-2 border transition-colors ${active ? '' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
              <FileJson className="h-4 w-4" />{c.name}
            </button>
          );
        })}
      </div>

      {selected && (
        <>
          <Card className="mb-6">
            <CardContent className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{selected.parsed.metadata.title}</h2>
                  <ModuleBadge module={selected.module} />
                </div>
                {selected.parsed.metadata.description && <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{selected.parsed.metadata.description}</p>}
                <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">{endpoints.length} endpoint(s) affiché(s)</p>
              </div>
              <FileJson className="h-6 w-6 text-blue-500 shrink-0" />
            </CardContent>
          </Card>

          <SearchBar value={search} onChange={setSearch} placeholder="Rechercher un endpoint…" className="mb-4 max-w-md" />

          <div className="flex flex-wrap gap-2 mb-6">
            {categories.map((cat) => {
              const active = category === cat;
              const CatIcon = categoryIcon(cat);
              return (
                <button key={cat} onClick={() => setCategory(cat)}
                  className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap flex items-center gap-1 border transition-colors ${active ? 'bg-blue-500 text-white border-blue-500' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                  {cat !== 'all' && <CatIcon className="h-3.5 w-3.5" />}{cat === 'all' ? 'Toutes' : cat}
                </button>
              );
            })}
          </div>

          {endpoints.length > 0 ? (
            endpoints.map((ep, i) => <EndpointDoc key={`${ep.category}-${ep.id}-${i}`} endpoint={ep} serverConfig={serverConfig} authToken={authToken} onAuthTokenUpdate={saveToken} />)
          ) : (
            <EmptyState icon={AlertTriangle} title="Aucun endpoint" message="Aucun endpoint ne correspond aux critères." />
          )}
        </>
      )}
    </div>
  );
};

export default DocumentationAPI;
